import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  /** True once we're inside the 300ms fade-out window. Adds the
   *  `.dismissing` class so the toast visually fades + becomes
   *  pointer-events: none right before being unmounted. */
  dismissing?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType) => void;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const MAX_VISIBLE = 3;
const DURATION_MS: Record<ToastType, number> = { success: 3000, info: 4000, error: 6000 };

const TOAST_ICONS: Record<ToastType, string> = {
  success: '\u2713', // ✓
  error: '\u26A0', // ⚠
  info: '\u2139', // ℹ
};

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = ++nextId;
    setToasts((prev) => {
      const next = [...prev, { id, message, type }];
      // Cap visible toasts — drop oldest when over limit
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    // Per-type duration drives BOTH the fade-out start (T - 300ms)
    // and the final unmount (T). Without this, the previous CSS-
    // hardcoded 2.7s fade left toasts visually gone but still
    // mounted blocking clicks for the rest of the duration.
    const total = DURATION_MS[type];
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, dismissing: true } : t)));
    }, total - 300);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, total);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}${t.dismissing ? ' dismissing' : ''}`} role="alert">
            <span className="toast-icon">{TOAST_ICONS[t.type]}</span>
            <span className="toast-msg">{t.message}</span>
            <button className="toast-dismiss" onClick={() => dismissToast(t.id)} aria-label="Dismiss">
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
